import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { ShieldCheck, Lock, BarChart3, Layers } from "lucide-react";
import { api } from "@/lib/api";
import { XentraWordmark } from "@/components/XentraLogo";

export default function Login() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && user) navigate("/app/dashboard", { replace: true });
  }, [user, loading, navigate]);

  const onMicrosoftSignIn = async () => {
    try {
      const r = await api.get(`/auth/microsoft/login?flow=staff&origin=${encodeURIComponent(window.location.origin)}`);
      window.location.href = r.data.authorization_url;
    } catch (e) {
      console.error("Microsoft login failed", e);
    }
  };

  return (
    <div className="min-h-screen w-full grid lg:grid-cols-2 bg-white" data-testid="login-page">
      {/* Left: brand panel */}
      <div className="relative hidden lg:flex flex-col justify-between bg-slate-950 text-white p-12 overflow-hidden">
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage:
              "url(https://images.unsplash.com/photo-1639322537228-f710d846310a?crop=entropy&cs=srgb&fm=jpg&q=80&w=1600)",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950/90 via-slate-950/70 to-blue-950/80" />

        <div className="relative z-10 flex items-center gap-3">
          <XentraWordmark variant="white" size={10} />
          <div className="text-xs uppercase tracking-[0.25em] text-blue-300 font-mono">CRM</div>
        </div>

        <div className="relative z-10">
          <h1 className="font-heading text-4xl xl:text-5xl font-bold leading-tight tracking-tight">
            One Account.
            <br />
            <span className="text-blue-400">Every customer signal.</span>
          </h1>
          <p className="mt-6 text-slate-300 text-base max-w-md leading-relaxed">
            Revenue, deals, contracts, billing, support and Stax adoption in a single
            workspace built for Xentra's cyber security and safeguarding teams.
          </p>

          <div className="mt-12 grid grid-cols-2 gap-4 max-w-md">
            {[
              { icon: ShieldCheck, label: "Account 360°" },
              { icon: BarChart3, label: "Pipeline & GP" },
              { icon: Layers, label: "Stax Adoption" },
              { icon: Lock, label: "Role-based access" },
            ].map(({ icon: Icon, label }) => (
              <div
                key={label}
                className="flex items-center gap-3 rounded-md border border-white/10 bg-white/5 backdrop-blur px-4 py-3"
              >
                <Icon className="h-4 w-4 text-blue-400" />
                <span className="text-sm font-medium text-slate-100">{label}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="relative z-10 text-xs text-slate-400 font-mono">
          xentra.co.uk · UK cyber security & safeguarding
        </div>
      </div>

      {/* Right: sign-in */}
      <div className="flex items-center justify-center p-8 sm:p-12 bg-white">
        <div className="w-full max-w-sm animate-fade-up">
          <div className="lg:hidden flex items-center gap-3 mb-10">
            <XentraWordmark variant="dark" size={8} />
            <div className="text-xs uppercase tracking-[0.25em] text-blue-700 font-mono">CRM</div>
          </div>

          <div className="text-xs uppercase tracking-[0.2em] text-blue-700 font-mono mb-4">
            Sign in to continue
          </div>
          <h2 className="font-heading text-3xl font-bold text-slate-900 leading-tight">
            Welcome back.
          </h2>
          <p className="mt-3 text-slate-500 text-sm leading-relaxed">
            Use your Xentra Microsoft 365 account to access the CRM workspace.
          </p>

          <Button
            data-testid="login-microsoft-btn"
            onClick={onMicrosoftSignIn}
            className="mt-10 w-full h-12 bg-white text-slate-900 border border-slate-300 hover:bg-slate-50 hover:border-slate-400 shadow-sm transition-all flex items-center justify-center gap-3 rounded-md"
          >
            <svg className="h-5 w-5" viewBox="0 0 23 23" aria-hidden="true">
              <rect x="1" y="1" width="10" height="10" fill="#F25022" />
              <rect x="12" y="1" width="10" height="10" fill="#7FBA00" />
              <rect x="1" y="12" width="10" height="10" fill="#00A4EF" />
              <rect x="12" y="12" width="10" height="10" fill="#FFB900" />
            </svg>
            <span className="font-medium">Continue with Microsoft</span>
          </Button>

          <div className="mt-8 text-xs text-slate-400 leading-relaxed">
            By signing in you agree to Xentra's internal usage policy. Sessions persist for 7 days.
          </div>

          <div className="mt-16 pt-6 border-t border-slate-100 flex items-center justify-between text-xs text-slate-400">
            <span className="font-mono">v0.1 · MVP</span>
            <a
              href="https://www.xentra.co.uk"
              target="_blank"
              rel="noreferrer"
              className="hover:text-blue-700 transition-colors"
            >
              xentra.co.uk →
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
